import { MyClaims } from "@/components/ClaimStatus";
import { MyPieces } from "@/components/MyPieces";
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProductCard } from "@/components/ProductCard";
import { PageHeader, Field, inputCls, PasswordInput } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";
import { VipAccount } from "@/components/VipAccount";

export const STATUS_LABEL: Record<string, string> = { pending_payment: "Awaiting payment", confirmed: "Confirmed", in_production: "In the atelier", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled" };

function Orders() {
  const [orders, setOrders] = useState<any[] | null>(null);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  if (!orders) return null;
  if (!orders.length) return <p className="text-sm text-neutral-500 py-10" data-testid="orders-empty">You haven't placed any orders yet.</p>;
  return (
    <div className="space-y-5" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.id} className="border border-neutral-200 bg-white p-6" data-testid={`order-${o.number}`}>
          <div className="flex flex-wrap justify-between gap-4">
            <div><p className="font-display text-xl">Order {o.number}</p><p className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleDateString("en-AU", { dateStyle: "long" })}</p></div>
            <div className="text-right"><p className="eyebrow text-[var(--c-primary)]" data-testid="order-status">{o.payment_status === "awaiting_transfer" && o.status === "pending_payment" ? "Awaiting bank transfer" : STATUS_LABEL[o.status]}</p><p className="text-sm">{money(o.total)}</p></div>
          </div>
          <div className="flex gap-3 mt-4">{o.items.map((i: any, n: number) => <img key={n} src={i.image} alt={i.name} title={i.name} className="h-16 w-16 object-contain bg-[var(--c-muted)]" />)}</div>
          {o.tracking?.number && <p className="text-xs mt-3">Tracking: {o.tracking.carrier} {o.tracking.number}</p>}
          <Link to={`/track?order=${o.number}`} className="text-xs underline mt-3 inline-block">View timeline</Link>
        </div>
      ))}
    </div>
  );
}

function Wishlist() {
  const [items, setItems] = useState<any[] | null>(null);
  const { wishlist } = useAuth();
  useEffect(() => { api.get("/wishlist").then((r) => setItems(r.data.items)); }, [wishlist.length]);
  if (!items) return null;
  if (!items.length) return <p className="text-sm text-neutral-500 py-10" data-testid="wishlist-empty">Your wishlist is empty. Tap the heart on any piece to save it.</p>;
  return <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10" data-testid="wishlist-grid">{items.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}</div>;
}

function Profile() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name || "", phone: user.phone || "", location: user.location || "", birthday: user.birthday || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    try { const r = await api.put("/auth/me", f); setUser(r.data); toast("Profile saved"); } catch (x) { toast.error(errMsg(x)); }
  };
  const changePw = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", pw); setPw({ current_password: "", new_password: "" }); toast("Password changed"); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-14 max-w-4xl">
    <form onSubmit={save} className="space-y-6" data-testid="profile-form">
      <Field label="Email"><input disabled value={user.email} className={inputCls + " opacity-60"} /></Field>
      <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="profile-name-input" /></Field>
      <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="profile-phone-input" /></Field>
      <Field label="Location"><input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={inputCls} data-testid="profile-location-input" /></Field>
      <Field label="Birthday"><input type="date" value={f.birthday} onChange={(e) => setF({ ...f, birthday: e.target.value })} className={inputCls} data-testid="profile-birthday-input" /></Field>
      <button className="btn-ink px-9 py-3.5 text-xs tracking-[0.25em] uppercase" data-testid="profile-save-button">Save</button>
    </form>
    <form onSubmit={changePw} className="space-y-6" data-testid="change-password-form">
      <p className="font-display text-2xl">Change password</p>
      <Field label="Current password"><PasswordInput required value={pw.current_password} onChange={(e: any) => setPw({ ...pw, current_password: e.target.value })} testId="current-password-input" /></Field>
      <Field label="New password (min 8)"><PasswordInput required minLength={8} value={pw.new_password} onChange={(e: any) => setPw({ ...pw, new_password: e.target.value })} testId="new-password-input" /></Field>
      <button className="btn-line px-9 py-3.5 text-xs tracking-[0.25em] uppercase" data-testid="change-password-button">Update Password</button>
    </form>
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "orders";
  useMeta("My Account | Somnio.Co");
  const tabs = [["orders", "Orders"], ["pieces", "My Pieces"], ["claims", "Warranty Claims"], ["vip", "Somnio VIP"], ["wishlist", "Wishlist"], ["profile", "Profile"]];
  return (
    <div data-testid="account-page">
      <PageHeader eyebrow="My Account" title={`Hello, ${user.name?.split(" ")[0] || ""}`} />
      <div className="max-w-[1400px] mx-auto px-5 md:px-12">
        <div className="flex flex-wrap items-end border-b border-neutral-200 mb-10">
          <div className="order-2 md:order-1 flex gap-7 md:gap-8 overflow-x-auto whitespace-nowrap w-full md:w-auto [scrollbar-width:none]" data-testid="account-tabs">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setParams({ tab: k })} data-testid={`account-tab-${k}`} className={`eyebrow flex-none pb-4 border-b ${tab === k ? "border-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"}`}>{l}</button>
            ))}
          </div>
          <div className="order-1 md:order-2 w-full md:w-auto md:ml-auto flex justify-end gap-6 pb-4">
            {user.role === "admin" && <Link to="/admin" className="eyebrow underline" data-testid="account-admin-link">Admin</Link>}
            <button onClick={logout} className="eyebrow text-neutral-500 hover:text-neutral-900" data-testid="logout-button">Sign out</button>
          </div>
        </div>
        {tab === "orders" && <Orders />}
        {tab === "pieces" && <MyPieces />}
        {tab === "claims" && <MyClaims />}
        {tab === "vip" && <VipAccount />}
        {tab === "wishlist" && <Wishlist />}
        {tab === "profile" && <Profile />}
      </div>
    </div>
  );
}
